import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Kept out of `button.tsx` so that file exports only a component. Otherwise
 * React Fast Refresh gives up on it and every edit reloads the whole app.
 *
 * Shape lock: every size uses `rounded-control`. A button never
 * invents its own corner.
 * Tactile feedback: `:active` nudges the control down one pixel, which is the
 * only motion a button gets.
 */
export const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'rounded-control text-sm font-medium',
    'transition-[background-color,border-color,color,translate] duration-150',
    'active:translate-y-px',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
    'disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ].join(' '),
  {
    variants: {
      variant: {
        // Accent on accent-foreground clears WCAG AA in both themes; the token
        // pair exists so neither side can drift without the other.
        primary: 'bg-accent text-accent-foreground hover:bg-accent/90',
        outline: 'border border-border-strong bg-surface text-foreground hover:bg-surface-sunken',
        ghost: 'text-muted-foreground hover:bg-surface-sunken hover:text-foreground',
        danger: 'bg-danger text-danger-foreground hover:bg-danger/90',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-9 px-4',
        lg: 'h-10 px-5',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export type ButtonVariantProps = VariantProps<typeof buttonVariants>;
